import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Flag,
  AlertCircle,
  Loader,
} from "lucide-react";
import { apiService } from "../services/api";
import CodeEditor from "../components/CodeEditor";
import styles from "./Assessment.module.css";

const Assessment = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [codeAnswers, setCodeAnswers] = useState({}); // For coding questions
  const [codeOutputs, setCodeOutputs] = useState({}); // Store execution outputs
  const [isRunningCode, setIsRunningCode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [attemptId, setAttemptId] = useState(null);

  // Fetch assessment data from API
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        setError(null);

        const response =
          await apiService.assessments.getAssessment(assessmentId);

        if (response && !response.error) {
          setCurrentAssessment(response);
        } else {
          throw new Error(response?.error || "Assessment not found");
        }
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError(err.message || "Failed to load assessment");
      } finally {
        setLoading(false);
      }
    };

    if (assessmentId) {
      fetchAssessment();
    }
  }, [assessmentId]);

  useEffect(() => {
    if (isStarted && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && isStarted) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, isStarted]);

  const startAssessment = () => {
    setIsStarted(true);
    setStartTime(new Date()); // Track when assessment started
    // Convert time limit from seconds to seconds (API returns seconds)
    const timeInSeconds = currentAssessment.timeLimit || 30 * 60; // Default 30 minutes
    setTimeLeft(timeInSeconds);
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers({
      ...answers,
      [questionId]: answerIndex,
    });
  };

  const handleCodeChange = (questionId, code) => {
    setCodeAnswers({
      ...codeAnswers,
      [questionId]: code,
    });
  };

  const handleRunCode = async (code, language) => {
    const question = currentAssessment.questions[currentQuestion];

    try {
      setIsRunningCode(true);

      // Call the code execution API
      const response = await apiService.code.executeCode({
        code,
        language,
        testCases: question.testCases || [],
      });

      setCodeOutputs({
        ...codeOutputs,
        [question.id]:
          response.output || response.stdout || "Code executed successfully",
      });
    } catch (error) {
      console.error("Code execution error:", error);
      setCodeOutputs({
        ...codeOutputs,
        [question.id]: `Error: ${error.message || "Failed to execute code"}`,
      });
    } finally {
      setIsRunningCode(false);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < currentAssessment.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setSubmitting(true);

      // Calculate duration if start time is available
      const duration = startTime
        ? Math.floor((new Date() - startTime) / 1000)
        : null;

      // Convert answers to the format expected by the API
      const formattedAnswers = {};
      currentAssessment.questions.forEach((question) => {
        // Handle coding questions
        if (
          question.questionType === "coding" ||
          question.questionType === "code"
        ) {
          if (codeAnswers[question.id]) {
            formattedAnswers[question.id] = codeAnswers[question.id];
          }
        } else {
          // Handle MCQ questions
          const answerIndex = answers[question.id];
          if (
            answerIndex !== undefined &&
            question.options &&
            question.options[answerIndex]
          ) {
            formattedAnswers[question.id] =
              question.options[answerIndex].option_text;
          }
        }
      });

      const response = await apiService.assessments.submitAssessment(
        assessmentId,
        formattedAnswers,
        { startedAt: startTime?.toISOString(), duration: duration },
      );

      if (response && !response.error) {
        setScore(response.attempt.percentage);
        // Calculate correct answers based on the actual score and max score
        const totalQuestions = currentAssessment.questions.length;
        const pointsPerQuestion = response.attempt.maxScore / totalQuestions;
        const correctAnswersCount = Math.round(
          response.attempt.score / pointsPerQuestion,
        );
        setCorrectCount(correctAnswersCount);
        setAttemptId(response.attempt.id || response.id); // Store attempt ID for detailed report
        setIsCompleted(true);
      } else {
        throw new Error(response?.error || "Failed to submit assessment");
      }
    } catch (err) {
      console.error("Error submitting assessment:", err);
      alert("Failed to submit assessment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className={styles.assessmentLoading}>
        <div className={styles.assessmentContainer}>
          <div className={styles.loadingState}>
            <Loader size={48} className={styles.spinner} />
            <h3>Loading assessment...</h3>
            <p>Please wait while we fetch the assessment details</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !currentAssessment) {
    return (
      <div className={styles.assessmentNotFound}>
        <div className={styles.assessmentContainer}>
          <div className={styles.errorState}>
            <AlertCircle size={48} />
            <h2>Assessment Not Found</h2>
            <p>{error || "The requested assessment could not be found."}</p>
            <button
              onClick={() => navigate("/assessments")}
              className="btn btn-primary"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    // Use the actual correct count from API, fallback to calculation if not available
    const correctAnswers =
      correctCount > 0
        ? correctCount
        : Math.round((score * currentAssessment.questions.length) / 100);
    const incorrectAnswers =
      currentAssessment.questions.length - correctAnswers;
    const performanceLevel =
      score >= 80
        ? "excellent"
        : score >= 60
          ? "good"
          : score >= 40
            ? "average"
            : "needs-improvement";
    const performanceMessage = {
      excellent: {
        title: "Outstanding!",
        message: "You've mastered this topic!",
        emoji: "🎉",
      },
      good: {
        title: "Well Done!",
        message: "You're on the right track!",
        emoji: "👏",
      },
      average: {
        title: "Good Effort!",
        message: "Keep practicing to improve!",
        emoji: "💪",
      },
      "needs-improvement": {
        title: "Keep Going!",
        message: "Practice makes perfect!",
        emoji: "📚",
      },
    }[performanceLevel];

    return (
      <div className={styles.assessmentCompleted}>
        <div className={styles.assessmentContainer}>
          <div className={styles.completionCard}>
            {/* Success Icon with Animation */}
            <div
              className={`${styles.completionIconWrapper} ${styles[performanceLevel]}`}
            >
              <div className={styles.completionIcon}>
                <CheckCircle size={56} />
              </div>
              <div className={styles.iconPulse}></div>
            </div>

            {/* Main Header */}
            <div className={styles.completionHeader}>
              <h1>Assessment Completed!</h1>
              <p className={styles.completionSubtitle}>
                {currentAssessment.title}
              </p>
            </div>

            {/* Score Display - Main Focus */}
            <div className={`score-showcase ${performanceLevel}`}>
              <div className={styles.scoreCircleContainer}>
                <svg className={styles.scoreRing} viewBox="0 0 120 120">
                  <circle
                    className={styles.scoreRingBackground}
                    cx="60"
                    cy="60"
                    r="54"
                  />
                  <circle
                    className={styles.scoreRingProgress}
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(score / 100) * 339.292} 339.292`,
                    }}
                  />
                </svg>
                <div className={styles.scoreContent}>
                  <span className={styles.scoreNumber}>{score}</span>
                  <span className={styles.scorePercent}>%</span>
                </div>
              </div>
              <div className={styles.scoreMessage}>
                <h2>
                  {performanceMessage.title} {performanceMessage.emoji}
                </h2>
                <p>{performanceMessage.message}</p>
              </div>
            </div>

            {/* Detailed Statistics */}
            <div className={styles.completionStatsGrid}>
              <div className={styles.statCard}>
                <div className={`${styles.statIcon} ${styles.correct}`}>
                  <CheckCircle size={24} />
                </div>
                <div className={styles.statContent}>
                  <span className={styles.statValue}>{correctAnswers}</span>
                  <span className={styles.statLabel}>Correct</span>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={`${styles.statIcon} ${styles.incorrect}`}>
                  <AlertCircle size={24} />
                </div>
                <div className={styles.statContent}>
                  <span className={styles.statValue}>{incorrectAnswers}</span>
                  <span className={styles.statLabel}>Incorrect</span>
                </div>
              </div>

              <div className={styles.statCard}>
                <div className={`${styles.statIcon} ${styles.total}`}>
                  <Flag size={24} />
                </div>
                <div className={styles.statContent}>
                  <span className={styles.statValue}>
                    {currentAssessment.questions.length}
                  </span>
                  <span className={styles.statLabel}>Total Questions</span>
                </div>
              </div>
            </div>

            {/* Assessment Info */}
            <div className={styles.completionInfo}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Skill Assessed</span>
                <span className={styles.infoValue}>
                  {currentAssessment.skillName}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Difficulty</span>
                <span className={styles.infoValue}>
                  {currentAssessment.difficultyLevel}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.completionActions}>
              <button
                onClick={() =>
                  navigate(`/assessment-report/${attemptId || assessmentId}`)
                }
                className="btn btn-primary btn-large"
              >
                <Flag size={20} />
                View Detailed Report
              </button>
              <div className={styles.secondaryActions}>
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-secondary"
                >
                  <ArrowLeft size={18} />
                  Retake Assessment
                </button>
                <button
                  onClick={() => navigate("/assessments")}
                  className="btn btn-outline"
                >
                  Browse Assessments
                </button>
              </div>
            </div>

            {/* Motivational Footer */}
            {score < 80 && (
              <div className={styles.completionTip}>
                <p>
                  <strong>Tip:</strong> Review the detailed report to understand
                  your mistakes and improve your score!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!isStarted) {
    return (
      <div className={styles.assessmentIntro}>
        <div className={styles.assessmentContainer}>
          <div className={styles.introCard}>
            <div className={styles.introHeader}>
              <h1>{currentAssessment.title}</h1>
              <p>{currentAssessment.description}</p>
            </div>

            <div className={styles.assessmentDetails}>
              <div className={styles.detailItem}>
                <Clock size={20} />
                <span>
                  Duration:{" "}
                  {Math.round((currentAssessment.timeLimit || 1800) / 60)}{" "}
                  minutes
                </span>
              </div>
              <div className={styles.detailItem}>
                <Flag size={20} />
                <span>Difficulty: {currentAssessment.difficultyLevel}</span>
              </div>
              <div className={styles.detailItem}>
                <AlertCircle size={20} />
                <span>Questions: {currentAssessment.questions.length}</span>
              </div>
            </div>

            <div className={styles.instructions}>
              <h3>Instructions:</h3>
              <ul>
                <li>
                  You have{" "}
                  {Math.round((currentAssessment.timeLimit || 1800) / 60)}{" "}
                  minutes to complete this assessment
                </li>
                <li>Each question has only one correct answer</li>
                <li>
                  You can navigate between questions using the navigation
                  buttons
                </li>
                <li>
                  Make sure to submit your assessment before time runs out
                </li>
                <li>Your progress will be saved automatically</li>
              </ul>
            </div>

            <div className={styles.introActions}>
              <button
                onClick={() => navigate("/assessments")}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={startAssessment} className="btn btn-primary">
                Start Assessment
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const question = currentAssessment.questions[currentQuestion];
  const progress =
    ((currentQuestion + 1) / currentAssessment.questions.length) * 100;

  return (
    <div className={styles.assessmentActive}>
      <div className={styles.assessmentContainer}>
        <div className={styles.assessmentHeader}>
          <div className={styles.assessmentInfo}>
            <h2>{currentAssessment.title}</h2>
            <div className={styles.questionCounter}>
              Question {currentQuestion + 1} of{" "}
              {currentAssessment.questions.length}
            </div>
          </div>
          <div className={styles.timer}>
            <Clock size={18} />
            <span className={timeLeft < 300 ? "timer-warning" : ""}>
              {formatTime(timeLeft)}
            </span>
          </div>
        </div>

        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        <div className={styles.questionCard}>
          <div className={styles.questionContent}>
            <h3>
              Q{currentQuestion + 1}. {question.questionText}
            </h3>

            {/* Render based on question type */}
            {question.questionType === "coding" ||
            question.questionType === "code" ? (
              <div className={styles.codingQuestion}>
                {question.description && (
                  <div className={styles.questionDescription}>
                    <p>{question.description}</p>
                  </div>
                )}

                <CodeEditor
                  language={
                    question.language ||
                    question.programmingLanguage ||
                    "javascript"
                  }
                  code={codeAnswers[question.id] || question.starterCode || ""}
                  onChange={(code) => handleCodeChange(question.id, code)}
                  onRun={handleRunCode}
                  output={codeOutputs[question.id] || ""}
                  isRunning={isRunningCode}
                  testCases={question.testCases || []}
                  height="500px"
                />

                {question.constraints && (
                  <div className={styles.questionConstraints}>
                    <h4>Constraints:</h4>
                    <ul>
                      {question.constraints.map((constraint, idx) => (
                        <li key={idx}>{constraint}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.optionsList}>
                {question.options &&
                  question.options.map((option, index) => (
                    <label key={index} className={styles.optionItem}>
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={index}
                        checked={answers[question.id] === index}
                        onChange={() => handleAnswer(question.id, index)}
                      />
                      <span className={styles.optionText}>
                        {option.option_text}
                      </span>
                    </label>
                  ))}
              </div>
            )}
          </div>

          <div className={styles.questionNavigation}>
            <button
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
              className="btn btn-secondary"
            >
              <ArrowLeft size={18} />
              Previous
            </button>

            <div className={styles.navInfo}>
              {Object.keys(answers).length} of{" "}
              {currentAssessment.questions.length} answered
            </div>

            {currentQuestion === currentAssessment.questions.length - 1 ? (
              <button
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Assessment"}
                <CheckCircle size={18} />
              </button>
            ) : (
              <button onClick={nextQuestion} className="btn btn-primary">
                Next
                <ArrowRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
